/**
 * `reduceProtocolFrame` — the pure Weave GUI protocol v1 reducer.
 *
 * ONE function folds every frame of §4's 31 event types into the `RunState` of
 * §3.2. It is pure: no store access, no fetch, no clock, no `Math.random`. The
 * store (`stores/runStateStore.ts`) and the stream hook are thin shells around
 * it, which is what makes a 60-frame write run a unit test instead of a manual
 * click-through.
 *
 * THE INVARIANT EVERY REDUCER HOLDS. `state.snapshot` is authoritative and
 * arrives first (§1.4.3), on subscribe AND on every reconnect. So a per-event
 * reducer's only job is to keep RunState equal to what the backend WOULD
 * snapshot at that moment — never to invent a field the snapshot does not
 * carry. Where an event has no home in RunState (a `tool.*` frame for a tool
 * call that has no `tool_call` part yet; the `diff` rider on
 * `changeset.component_updated`, since RunState holds no Diff) the reducer
 * DROPS it rather than growing a private side-store that the next snapshot
 * would silently contradict. Diffs are fetched by REST per §6.
 *
 * DESYNC, NOT GUESSING. `state.delta` carries RFC 6902 patches rooted at
 * RunState. A patch that cannot be applied — a gap in `base_seq`, a pointer
 * into a missing container, a failed `test` op — means this client's copy has
 * diverged from the server's. The reducer then returns the state UNCHANGED and
 * raises `desync`, and the caller re-reads `GET /api/runs/{id}/state`. Applying
 * half a patch, or best-effort-guessing the intent, would leave a store that
 * looks fine and is wrong, which is the failure mode the whole snapshot design
 * exists to prevent.
 */

import type {
  Changeset,
  Checkpoint,
  Component,
  Deploy,
  Error as ProtocolError,
  Evidence,
  Interaction,
  Message,
  Part,
  Phase,
  Plan,
  RunState,
  Task,
  Usage,
} from "./weave-protocol-v1";

// ───────────────────────────────────────────────────────────────────────────
// Frame shape
// ───────────────────────────────────────────────────────────────────────────

/**
 * A parsed protocol-1 SSE frame. Deliberately looser than the generated
 * `Envelope`: the generated `data: {}` type cannot be indexed, and everything
 * here came off a socket as untrusted JSON, so each reducer narrows what it
 * actually reads instead of trusting a cast.
 */
export interface ProtocolFrame {
  protocol_version?: string;
  event_type?: string;
  seq?: number;
  run_id?: string;
  session_id?: string;
  turn_id?: string;
  ts?: string;
  hop?: number | null;
  task_id?: string | null;
  data?: unknown;
}

/** What a reduction produced, plus whether the client must re-read `/state`. */
export interface ReduceOutcome {
  state: RunState | null;
  /** True when the frame could not be applied and `GET /state` is required. */
  desync: boolean;
  /** Human-readable cause, for logs and the reconnect banner. */
  reason?: string;
}

/** Transport frames: never durable, never replayed, carry no state (§1.5). */
export const TRANSPORT_EVENT_TYPES: ReadonlySet<string> = new Set(["subscribed", "heartbeat", "ping"]);

/** The frame that closes the stream (§1.4.7). */
export const TERMINAL_EVENT_TYPE = "lifecycle.run_finished";

// ───────────────────────────────────────────────────────────────────────────
// Small typed helpers over untrusted JSON
// ───────────────────────────────────────────────────────────────────────────

type Dict = Record<string, unknown>;

function isObj(value: unknown): value is Dict {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isArr(value: unknown): value is unknown[] {
  return Array.isArray(value);
}

function asObj(value: unknown): Dict {
  return isObj(value) ? value : {};
}

function str(value: unknown): string | undefined {
  return typeof value === "string" ? value : undefined;
}

function num(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

/** Drop `undefined` values so a partial payload never blanks a known field. */
function defined(source: Dict): Dict {
  const out: Dict = {};
  for (const key of Object.keys(source)) {
    if (source[key] !== undefined) out[key] = source[key];
  }
  return out;
}

function deepEqual(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (isArr(a) && isArr(b)) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) if (!deepEqual(a[i], b[i])) return false;
    return true;
  }
  if (isObj(a) && isObj(b)) {
    const ak = Object.keys(a);
    const bk = Object.keys(b);
    if (ak.length !== bk.length) return false;
    for (const key of ak) {
      if (!Object.prototype.hasOwnProperty.call(b, key)) return false;
      if (!deepEqual(a[key], b[key])) return false;
    }
    return true;
  }
  return false;
}

function deepClone<T>(value: T): T {
  if (isArr(value)) return value.map((item) => deepClone(item)) as unknown as T;
  if (isObj(value)) {
    const out: Dict = {};
    for (const key of Object.keys(value)) out[key] = deepClone(value[key]);
    return out as unknown as T;
  }
  return value;
}

// ───────────────────────────────────────────────────────────────────────────
// RFC 6902 — JSON Pointer + JSON Patch, implemented locally
// ───────────────────────────────────────────────────────────────────────────
//
// Local rather than a dependency because the surface actually used is six ops
// over a known-shaped document, and because the CSP-ish constraint that matters
// here is bundle weight on a flag-gated path. Application is structurally
// sharing: only the containers ON the pointer's path are cloned, so a delta
// touching `/changeset/components/3/status` leaves `state.messages` reference-
// identical and every unrelated fine-grained selector skips its re-render.

export class JsonPatchError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "JsonPatchError";
  }
}

export interface PatchOperation {
  op: "add" | "remove" | "replace" | "move" | "copy" | "test";
  path: string;
  from?: string;
  value?: unknown;
}

/** `~1` → `/` and `~0` → `~`, in that order (RFC 6901 §4). */
function unescapeToken(token: string): string {
  if (token.indexOf("~") === -1) return token;
  let out = "";
  for (let i = 0; i < token.length; i++) {
    if (token[i] === "~") {
      const next = token[i + 1];
      if (next === "0") {
        out += "~";
        i++;
        continue;
      }
      if (next === "1") {
        out += "/";
        i++;
        continue;
      }
    }
    out += token[i];
  }
  return out;
}

/** JSON Pointer → tokens. `""` is the whole document. */
export function parsePointer(pointer: string): string[] {
  if (pointer === "") return [];
  if (pointer[0] !== "/") {
    throw new JsonPatchError(`pointer must be "" or start with "/": ${pointer}`);
  }
  return pointer.slice(1).split("/").map(unescapeToken);
}

/** Strict non-negative integer, no leading zeros — array indices only. */
function toIndex(token: string): number | null {
  if (token.length === 0) return null;
  if (token.length > 1 && token[0] === "0") return null;
  let out = 0;
  for (let i = 0; i < token.length; i++) {
    const digit = token.charCodeAt(i) - 48;
    if (digit < 0 || digit > 9) return null;
    out = out * 10 + digit;
  }
  return out;
}

/** Read the value a pointer addresses; throws when any segment is missing. */
export function getPointer(doc: unknown, tokens: string[]): unknown {
  let cursor: unknown = doc;
  for (const token of tokens) {
    if (isArr(cursor)) {
      const idx = toIndex(token);
      if (idx === null || idx >= cursor.length) {
        throw new JsonPatchError(`array index out of range: ${token}`);
      }
      cursor = cursor[idx];
      continue;
    }
    if (isObj(cursor)) {
      if (!Object.prototype.hasOwnProperty.call(cursor, token)) {
        throw new JsonPatchError(`missing member: ${token}`);
      }
      cursor = cursor[token];
      continue;
    }
    throw new JsonPatchError(`cannot descend into a non-container at ${token}`);
  }
  return cursor;
}

/** Rewrite the leaf container a pointer lands in. */
type LeafWriter = (container: unknown, token: string) => unknown;

function writeAtPath(root: unknown, tokens: string[], write: LeafWriter): unknown {
  if (tokens.length === 1) return write(root, tokens[0]);
  const [head, ...rest] = tokens;
  if (isArr(root)) {
    const idx = toIndex(head);
    if (idx === null || idx >= root.length) {
      throw new JsonPatchError(`array index out of range: ${head}`);
    }
    const next = root.slice();
    next[idx] = writeAtPath(root[idx], rest, write);
    return next;
  }
  if (isObj(root)) {
    if (!Object.prototype.hasOwnProperty.call(root, head)) {
      throw new JsonPatchError(`missing member: ${head}`);
    }
    return { ...root, [head]: writeAtPath(root[head], rest, write) };
  }
  throw new JsonPatchError(`cannot descend into a non-container at ${head}`);
}

function addWriter(value: unknown): LeafWriter {
  return (container, token) => {
    if (isArr(container)) {
      const idx = token === "-" ? container.length : toIndex(token);
      if (idx === null || idx > container.length) {
        throw new JsonPatchError(`array index out of range for add: ${token}`);
      }
      const next = container.slice();
      next.splice(idx, 0, value);
      return next;
    }
    if (isObj(container)) return { ...container, [token]: value };
    throw new JsonPatchError(`cannot add to a non-container at ${token}`);
  };
}

function replaceWriter(value: unknown): LeafWriter {
  return (container, token) => {
    if (isArr(container)) {
      const idx = toIndex(token);
      if (idx === null || idx >= container.length) {
        throw new JsonPatchError(`array index out of range for replace: ${token}`);
      }
      const next = container.slice();
      next[idx] = value;
      return next;
    }
    if (isObj(container)) {
      if (!Object.prototype.hasOwnProperty.call(container, token)) {
        throw new JsonPatchError(`cannot replace a missing member: ${token}`);
      }
      return { ...container, [token]: value };
    }
    throw new JsonPatchError(`cannot replace inside a non-container at ${token}`);
  };
}

const removeWriter: LeafWriter = (container, token) => {
  if (isArr(container)) {
    const idx = toIndex(token);
    if (idx === null || idx >= container.length) {
      throw new JsonPatchError(`array index out of range for remove: ${token}`);
    }
    const next = container.slice();
    next.splice(idx, 1);
    return next;
  }
  if (isObj(container)) {
    if (!Object.prototype.hasOwnProperty.call(container, token)) {
      throw new JsonPatchError(`cannot remove a missing member: ${token}`);
    }
    const next = { ...container };
    delete next[token];
    return next;
  }
  throw new JsonPatchError(`cannot remove inside a non-container at ${token}`);
};

function isPrefixOf(prefix: string[], tokens: string[]): boolean {
  if (prefix.length > tokens.length) return false;
  for (let i = 0; i < prefix.length; i++) if (prefix[i] !== tokens[i]) return false;
  return true;
}

const PATCH_OPS: ReadonlySet<string> = new Set(["add", "remove", "replace", "move", "copy", "test"]);

/** Apply ONE operation, returning a new document. Throws `JsonPatchError`. */
export function applyPatchOperation(doc: unknown, operation: PatchOperation): unknown {
  // Validate the operation itself BEFORE the pointer: a frame carrying
  // `{op:"bogus"}` or no `path` is malformed input off a socket, and it must
  // surface as a desync, not as a TypeError inside the pointer parser.
  if (!isObj(operation) || !PATCH_OPS.has(String(operation.op))) {
    throw new JsonPatchError(`unsupported op: ${String(isObj(operation) ? operation.op : operation)}`);
  }
  if (typeof operation.path !== "string") {
    throw new JsonPatchError('patch operation requires a string "path"');
  }
  const tokens = parsePointer(operation.path);
  switch (operation.op) {
    case "test": {
      const actual = getPointer(doc, tokens);
      if (!deepEqual(actual, operation.value)) {
        throw new JsonPatchError(`test failed at ${operation.path}`);
      }
      return doc;
    }
    case "add":
      if (tokens.length === 0) return operation.value;
      return writeAtPath(doc, tokens, addWriter(operation.value));
    case "replace":
      if (tokens.length === 0) return operation.value;
      return writeAtPath(doc, tokens, replaceWriter(operation.value));
    case "remove":
      if (tokens.length === 0) throw new JsonPatchError("cannot remove the whole document");
      return writeAtPath(doc, tokens, removeWriter);
    case "copy": {
      if (operation.from === undefined) throw new JsonPatchError('copy requires "from"');
      const value = deepClone(getPointer(doc, parsePointer(operation.from)));
      if (tokens.length === 0) return value;
      return writeAtPath(doc, tokens, addWriter(value));
    }
    case "move": {
      if (operation.from === undefined) throw new JsonPatchError('move requires "from"');
      const fromTokens = parsePointer(operation.from);
      if (isPrefixOf(fromTokens, tokens) && fromTokens.length < tokens.length) {
        throw new JsonPatchError("cannot move a value into its own child");
      }
      const value = getPointer(doc, fromTokens);
      const without =
        fromTokens.length === 0
          ? undefined
          : writeAtPath(doc, fromTokens, removeWriter);
      if (tokens.length === 0) return value;
      return writeAtPath(without, tokens, addWriter(value));
    }
    default:
      throw new JsonPatchError(`unsupported op: ${String((operation as PatchOperation).op)}`);
  }
}

/**
 * Apply a whole patch. ATOMIC by construction: every operation builds a new
 * document off the previous one and the caller only ever sees the final value,
 * so a throw half-way leaves the caller's original document untouched.
 */
export function applyJsonPatch<T>(doc: T, patch: readonly PatchOperation[]): T {
  let out: unknown = doc;
  for (const operation of patch) out = applyPatchOperation(out, operation);
  return out as T;
}

// ───────────────────────────────────────────────────────────────────────────
// RunState normalization
// ───────────────────────────────────────────────────────────────────────────

const DEFAULT_USAGE: Usage = {
  tokens_in: 0,
  tokens_out: 0,
  cost_usd: 0,
  tool_calls: 0,
  duration_ms: 0,
};

/**
 * Fill in the containers every reducer indexes into. A snapshot from a healthy
 * backend already has them; one from a partial replay, an older build, or a
 * hand-written fixture may not, and `state.messages.map` on `undefined` is a
 * white screen for a field the user was never going to look at.
 */
export function normalizeRunState(input: unknown): RunState {
  const raw = asObj(input);
  const run = asObj(raw.run);
  const interactions = asObj(raw.interactions);
  const actions = asObj(raw.actions);
  return {
    ...(raw as unknown as RunState),
    protocol_version: str(raw.protocol_version) ?? "1.0",
    run: {
      ...(run as unknown as RunState["run"]),
      run_id: str(run.run_id) ?? "",
      session_id: str(run.session_id) ?? "",
      turn_id: str(run.turn_id) ?? "",
      status: (str(run.status) ?? "queued") as RunState["run"]["status"],
      completed_with_gaps: run.completed_with_gaps === true,
      mode: (str(run.mode) ?? "read") as RunState["run"]["mode"],
      lane: (str(run.lane) ?? "reactive") as RunState["run"]["lane"],
      cancellable: run.cancellable === true,
      seq: num(run.seq) ?? 0,
    },
    messages: isArr(raw.messages) ? (raw.messages as Message[]) : [],
    tasks: isObj(raw.tasks) ? (raw.tasks as Record<string, Task>) : {},
    task_order: isArr(raw.task_order) ? (raw.task_order as string[]) : [],
    plan: isObj(raw.plan) ? (raw.plan as unknown as Plan) : null,
    changeset: isObj(raw.changeset) ? (raw.changeset as unknown as Changeset) : null,
    deploy: isObj(raw.deploy) ? (raw.deploy as unknown as Deploy) : null,
    checkpoints: isArr(raw.checkpoints) ? (raw.checkpoints as Checkpoint[]) : [],
    artifacts: isArr(raw.artifacts) ? (raw.artifacts as RunState["artifacts"]) : [],
    evidence: isObj(raw.evidence) ? (raw.evidence as Record<string, Evidence>) : {},
    interactions: {
      pending: str(interactions.pending) ?? null,
      byId: isObj(interactions.byId) ? (interactions.byId as Record<string, Interaction>) : {},
    },
    usage: isObj(raw.usage) ? (raw.usage as unknown as Usage) : DEFAULT_USAGE,
    errors: isArr(raw.errors) ? (raw.errors as ProtocolError[]) : [],
    actions: {
      primary: (str(actions.primary) ?? "none") as RunState["actions"]["primary"],
      available: isArr(actions.available) ? (actions.available as RunState["actions"]["available"]) : [],
      disabled: isArr(actions.disabled) ? (actions.disabled as RunState["actions"]["disabled"]) : [],
    },
  };
}

// ───────────────────────────────────────────────────────────────────────────
// Per-family reducers
// ───────────────────────────────────────────────────────────────────────────

function withRun(state: RunState, patch: Partial<RunState["run"]>): RunState {
  return { ...state, run: { ...state.run, ...defined(patch as Dict) } };
}

function mapMessages(
  state: RunState,
  messageId: string | undefined,
  fn: (message: Message) => Message,
): RunState {
  if (!messageId) return state;
  let hit = false;
  const messages = state.messages.map((message) => {
    if (message.message_id !== messageId) return message;
    hit = true;
    return fn(message);
  });
  return hit ? { ...state, messages } : state;
}

/** Rewrite the one part with this id, wherever it lives. */
function mapPart(state: RunState, partId: string | undefined, fn: (part: Part) => Part): RunState {
  if (!partId) return state;
  let hit = false;
  const messages = state.messages.map((message) => {
    if (!message.parts?.some((part) => part.part_id === partId)) return message;
    hit = true;
    return { ...message, parts: message.parts.map((part) => (part.part_id === partId ? fn(part) : part)) };
  });
  return hit ? { ...state, messages } : state;
}

/** Insert or merge a part, keeping `parts` ordered by `index`. */
function upsertPart(parts: Part[], incoming: Part): Part[] {
  const existing = parts.findIndex((part) => part.part_id === incoming.part_id);
  if (existing >= 0) {
    const next = parts.slice();
    next[existing] = { ...parts[existing], ...defined(incoming as unknown as Dict) } as Part;
    return next;
  }
  const next = parts.slice();
  const at = next.findIndex((part) => part.index > incoming.index);
  if (at < 0) next.push(incoming);
  else next.splice(at, 0, incoming);
  return next;
}

/** `text`/`reasoning` parts accumulate deltas into `body.text` (§2.5). */
function seedTextBody(part: Part): Part {
  if (part.type !== "text" && part.type !== "reasoning") return part;
  if (isObj(part.body) && typeof (part.body as Dict).text === "string") return part;
  return { ...part, body: { text: "" } };
}

function reduceMessage(state: RunState, eventType: string, data: Dict, frame: ProtocolFrame): RunState {
  switch (eventType) {
    case "message.start": {
      const messageId = str(data.message_id);
      if (!messageId) return state;
      if (state.messages.some((message) => message.message_id === messageId)) return state;
      const message: Message = {
        message_id: messageId,
        role: (str(data.role) ?? "assistant") as Message["role"],
        turn_id: str(data.turn_id) ?? str(frame.turn_id) ?? "",
        task_id: str(data.task_id) ?? frame.task_id ?? null,
        status: "streaming",
        parts: [],
      };
      return { ...state, messages: [...state.messages, message] };
    }
    case "message.part_start": {
      const partId = str(data.part_id);
      const messageId = str(data.message_id);
      if (!partId || !messageId) return state;
      const incoming = seedTextBody({
        ...(data as unknown as Part),
        part_id: partId,
        message_id: messageId,
        index: num(data.index) ?? 0,
        type: (str(data.type) ?? "text") as Part["type"],
        state: (str(data.state) ?? "streaming") as Part["state"],
      });
      return mapMessages(state, messageId, (message) => ({
        ...message,
        parts: upsertPart(message.parts ?? [], incoming),
      }));
    }
    case "message.part_delta": {
      const delta = str(data.delta);
      if (delta === undefined) return state;
      return mapPart(state, str(data.part_id), (part) => {
        const body = asObj(part.body);
        const text = typeof body.text === "string" ? body.text : "";
        return { ...part, state: "streaming", body: { ...body, text: text + delta } };
      });
    }
    case "message.part_end": {
      const incoming = defined(data);
      delete incoming.message_id;
      return mapPart(state, str(data.part_id), (part) => {
        // `body` MERGES rather than replaces. For a text part the two agree
        // (the closing body is the accumulated text). For a `tool_call` part
        // they do not: the tool lifecycle wrote `name`, `args` and the result
        // into this body, and a closing frame that carries only a summary must
        // not blank the call it is summarising.
        const body =
          isObj(data.body) && isObj(part.body)
            ? { ...asObj(part.body), ...asObj(data.body) }
            : incoming.body !== undefined
              ? data.body
              : part.body;
        return {
          ...part,
          ...(incoming as unknown as Partial<Part>),
          body: body as Part["body"],
          state: (str(data.state) ?? "available") as Part["state"],
        };
      });
    }
    case "message.end": {
      const status = str(data.status);
      return mapMessages(state, str(data.message_id), (message) => ({
        ...message,
        status: (status ?? "complete") as Message["status"],
      }));
    }
    /*
     * `message.queued` / `message.consumed` (contracts 0.2.8, S2) — a follow-up the reader sent
     * WHILE a turn was already running, accepted into the worker's hop-boundary inbox and later
     * drained into its message history. Deliberately a no-op here: `RunState` has no inbox field
     * for either event to write into (the schema's `Message`/`Part` shapes are the only things this
     * reducer mutates), so there is nothing to change until the eventual `message.start` for the
     * drained message arrives and gets handled above like any other. Explicit rather than falling
     * into `default` so the decision reads as deliberate, not missed.
     */
    case "message.queued":
    case "message.consumed":
      return state;
    default:
      return state;
  }
}

/**
 * Tool calls live as `tool_call` PARTS (§2.5) — RunState has no tools map — so
 * the `tool.*` family folds into the part the message stream already opened for
 * this call. A frame arriving before its part (or for a subagent transcript
 * this client is not holding) is dropped: the next snapshot carries the truth,
 * and a synthesised part would be a row the backend never agreed to.
 */
function reduceTool(state: RunState, eventType: string, data: Dict): RunState {
  const toolCallId = str(data.tool_call_id);
  if (!toolCallId) return state;
  let hit = false;
  const messages = state.messages.map((message) => {
    if (!message.parts?.length) return message;
    let changed = false;
    const parts = message.parts.map((part) => {
      if (part.type !== "tool_call") return part;
      const body = asObj(part.body);
      if (part.ref !== toolCallId && body.tool_call_id !== toolCallId) return part;
      changed = true;
      hit = true;
      switch (eventType) {
        case "tool.call_start":
          return {
            ...part,
            ref: part.ref ?? toolCallId,
            state: "streaming" as Part["state"],
            body: { ...body, ...defined(data), args_text: typeof body.args_text === "string" ? body.args_text : "" },
          };
        case "tool.call_args": {
          const previous = typeof body.args_text === "string" ? body.args_text : "";
          return { ...part, body: { ...body, args_text: previous + (str(data.delta) ?? "") } };
        }
        case "tool.call_end":
          return { ...part, body: { ...body, state: str(data.state) ?? body.state, args: data.args } };
        case "tool.call_result": {
          const failed = data.ok === false || str(data.state) === "output-error";
          return {
            ...part,
            state: (failed ? "error" : "available") as Part["state"],
            citations: isArr(data.citations) ? (data.citations as string[]) : part.citations,
            body: { ...body, ...defined(data) },
          };
        }
        default:
          return part;
      }
    });
    return changed ? { ...message, parts } : message;
  });
  return hit ? { ...state, messages } : state;
}

function reduceTask(state: RunState, eventType: string, data: Dict, frame: ProtocolFrame): RunState {
  const taskId = str(data.task_id);
  if (!taskId) return state;
  const previous = state.tasks[taskId];
  const incoming = defined(data) as unknown as Partial<Task>;
  const base: Task = previous ?? {
    task_id: taskId,
    role: (str(data.role) ?? "supervisor") as Task["role"],
    title: str(data.title) ?? taskId,
    status: "pending",
  };
  const next: Task = { ...base, ...incoming, task_id: taskId };
  if (eventType === "task.started" && !next.started_at) next.started_at = frame.ts ?? null;
  if (eventType === "task.finished") {
    if (!str(data.status)) next.status = "done";
    if (!next.finished_at) next.finished_at = frame.ts ?? null;
  }
  return {
    ...state,
    tasks: { ...state.tasks, [taskId]: next },
    task_order: previous || state.task_order.includes(taskId) ? state.task_order : [...state.task_order, taskId],
  };
}

function reducePlan(state: RunState, eventType: string, data: Dict): RunState {
  if (eventType === "plan.updated") {
    if (!isObj(data.plan)) return state;
    const incoming = data.plan as unknown as Plan;
    /*
     * NEVER LET AN OLDER VERSION OVERWRITE A NEWER ONE. `plan.updated` carries the whole plan and
     * every step status, and a replayed or late frame for version 3 landing after version 4 would
     * silently un-tick steps the reader watched complete. `plan_version` is the backend's own
     * monotonic counter per plan (`plan_store` appends versions), so a lower one for the SAME plan
     * is stale by definition. The same version again is idempotent — accept it, it may carry a
     * step status the earlier copy of it did not.
     */
    const current = state.plan;
    if (
      current &&
      current.plan_id === incoming.plan_id &&
      typeof current.plan_version === "number" &&
      typeof incoming.plan_version === "number" &&
      incoming.plan_version < current.plan_version
    ) {
      return state;
    }
    return { ...state, plan: incoming };
  }
  // plan.step_status
  const plan = state.plan;
  const stepId = str(data.step_id);
  const status = str(data.status);
  if (!plan || !stepId || !status) return state;
  const planId = str(data.plan_id);
  if (planId && plan.plan_id !== planId) return state;
  let hit = false;
  const steps = plan.steps.map((step) => {
    if (step.step_id !== stepId) return step;
    hit = true;
    return { ...step, status: status as Plan["steps"][number]["status"] };
  });
  return hit ? { ...state, plan: { ...plan, steps } } : state;
}

function reduceChangeset(state: RunState, eventType: string, data: Dict): RunState {
  switch (eventType) {
    case "changeset.updated":
      return isObj(data.changeset) ? { ...state, changeset: data.changeset as unknown as Changeset } : state;
    case "changeset.component_updated": {
      const changeset = state.changeset;
      const component = isObj(data.component) ? (data.component as unknown as Component) : null;
      if (!changeset || !component) return state;
      const changesetId = str(data.changeset_id);
      if (changesetId && changeset.changeset_id !== changesetId) return state;
      const components = changeset.components ?? [];
      const at = components.findIndex((existing) => existing.component_id === component.component_id);
      const next = at >= 0 ? components.slice() : [...components, component];
      if (at >= 0) next[at] = { ...components[at], ...component };
      // `data.diff` is deliberately dropped — RunState holds no Diff; §6's
      // GET /api/changesets/{id}/files/{file_id}/diff is the source of truth.
      return { ...state, changeset: { ...changeset, components: next } };
    }
    case "changeset.checkpoint_created": {
      const checkpointId = str(data.checkpoint_id);
      if (!checkpointId) return state;
      const checkpoints = state.checkpoints ?? [];
      if (checkpoints.some((checkpoint) => checkpoint.checkpoint_id === checkpointId)) return state;
      return { ...state, checkpoints: [...checkpoints, data as unknown as Checkpoint] };
    }
    default:
      return state;
  }
}

const ZERO_COUNTS: Phase["counts"] = { total: 0, succeeded: 0, failed: 0, skipped: 0, deferred: 0 };

function normalizePhase(input: unknown): Phase {
  const raw = asObj(input);
  return {
    ...(raw as unknown as Phase),
    ord: num(raw.ord) ?? 0,
    phase: (str(raw.phase) ?? "mdapi_check") as Phase["phase"],
    status: (str(raw.status) ?? "pending") as Phase["status"],
    counts: { ...ZERO_COUNTS, ...asObj(raw.counts) },
  };
}

function reduceDeploy(state: RunState, eventType: string, data: Dict): RunState {
  switch (eventType) {
    case "deploy.started": {
      const deploy: Deploy = {
        apply_id: str(data.apply_id) ?? state.deploy?.apply_id ?? "",
        changeset_id: str(data.changeset_id) ?? state.changeset?.changeset_id ?? "",
        kind: (str(data.kind) ?? "apply") as Deploy["kind"],
        state: "running",
        phases: (isArr(data.phases) ? data.phases : []).map(normalizePhase),
        deploy_id: null,
        checkpoint_id: str(data.checkpoint_id) ?? null,
        revert_available: false,
        attempt: num(data.attempt) ?? 1,
        max_attempts: num(data.max_attempts),
      };
      return { ...state, deploy };
    }
    case "deploy.phase": {
      const deploy = state.deploy;
      if (!deploy) return state;
      const applyId = str(data.apply_id);
      if (applyId && deploy.apply_id && deploy.apply_id !== applyId) return state;
      const incoming = normalizePhase(data);
      const phases = deploy.phases ?? [];
      const at = phases.findIndex(
        (phase) => phase.ord === incoming.ord || (phase.phase === incoming.phase && num(data.ord) === undefined),
      );
      const next = at >= 0 ? phases.slice() : [...phases, incoming];
      if (at >= 0) next[at] = { ...phases[at], ...incoming, counts: { ...phases[at].counts, ...incoming.counts } };
      next.sort((a, b) => a.ord - b.ord);
      return { ...state, deploy: { ...deploy, phases: next } };
    }
    case "deploy.finished": {
      const deploy = state.deploy;
      if (!deploy) return state;
      const incoming = defined(data) as unknown as Partial<Deploy>;
      return {
        ...state,
        deploy: {
          ...deploy,
          ...incoming,
          apply_id: deploy.apply_id,
          phases: deploy.phases,
          state: (str(data.state) ?? deploy.state) as Deploy["state"],
        },
      };
    }
    default:
      return state;
  }
}

function reduceInteraction(state: RunState, eventType: string, data: Dict): RunState {
  const byId = state.interactions.byId;
  switch (eventType) {
    case "interaction.required": {
      const interaction = isObj(data.interaction) ? (data.interaction as unknown as Interaction) : null;
      if (!interaction?.interaction_id) return state;
      return {
        ...state,
        interactions: {
          pending: interaction.interaction_id,
          byId: { ...byId, [interaction.interaction_id]: interaction },
        },
      };
    }
    case "interaction.resolved":
    case "interaction.expired": {
      const interactionId = str(data.interaction_id);
      if (!interactionId) return state;
      const previous = byId[interactionId];
      const status: Interaction["status"] = eventType === "interaction.resolved" ? "resolved" : "expired";
      const nextById = previous
        ? { ...byId, [interactionId]: { ...previous, status } }
        : byId;
      const next = {
        ...state,
        interactions: {
          pending: state.interactions.pending === interactionId ? null : state.interactions.pending,
          byId: nextById,
        },
      };
      /*
       * `resume_from` IS FOLDED IN, and it was being dropped.
       *
       * The released schema makes it REQUIRED on `ev.interaction.expired` — an expiry always carries
       * the handle for answering the question on a successor run (§5.3). It was discarded here, so
       * `run.resume_from` only ever appeared if a later `state.snapshot` happened to include it, and
       * the recovery card that reads it would not render on a live expiry — the exact case it exists
       * for. Found by vendoring the real contract release; the transcribed schema had no per-event
       * bodies to check against.
       *
       * `resolved` deliberately does NOT do this: its own `resume_from` (on a non-parked
       * `approve_plan`) comes back in the RESPOND response, where the caller that has to create the
       * run is already standing.
       */
      if (eventType === "interaction.expired" && isObj(data.resume_from)) {
        return withRun(next, { resume_from: data.resume_from as unknown as RunState["run"]["resume_from"] });
      }
      return next;
    }
    default:
      return state;
  }
}

function reduceLifecycle(state: RunState, eventType: string, data: Dict, frame: ProtocolFrame): RunState {
  switch (eventType) {
    case "lifecycle.run_started": {
      const next = isObj(data.org) ? { ...state, org: data.org as unknown as RunState["org"] } : state;
      // `capabilities` has no home in RunState (§3.2) — the backend expresses
      // what is possible through `actions`, which is the field the bar renders.
      return withRun(next, asObj(data.run) as Partial<RunState["run"]>);
    }
    case "lifecycle.run_status": {
      const interactionId = str(data.interaction_id);
      const next = withRun(state, {
        status: str(data.status) as RunState["run"]["status"] | undefined,
        cancellable: typeof data.cancellable === "boolean" ? data.cancellable : undefined,
      });
      if (!interactionId) return next;
      return { ...next, interactions: { ...next.interactions, pending: interactionId } };
    }
    case "lifecycle.run_finished": {
      let next = withRun(state, {
        status: (str(data.status) ?? "completed") as RunState["run"]["status"],
        outcome: (str(data.outcome) ?? state.run.outcome) as RunState["run"]["outcome"],
        completed_with_gaps:
          typeof data.completed_with_gaps === "boolean" ? data.completed_with_gaps : state.run.completed_with_gaps,
        finished_at: str(data.finished_at) ?? frame.ts ?? null,
        recovered: typeof data.recovered === "boolean" ? data.recovered : state.run.recovered,
        cancellable: false,
      });
      if (isObj(data.usage)) next = { ...next, usage: data.usage as unknown as Usage };
      if (isObj(data.error)) next = { ...next, errors: [...next.errors, data.error as unknown as ProtocolError] };
      /*
       * A run can finish holding a resume handle too — `ev.lifecycle.run_finished` carries an
       * optional `resume_from`. Same reason as the expiry case above: it is the only thing that
       * makes the recovery reachable, and dropping it leaves the reader on a finished run with a
       * question they cannot answer.
       */
      if (isObj(data.resume_from)) {
        next = withRun(next, { resume_from: data.resume_from as unknown as RunState["run"]["resume_from"] });
      }
      return next;
    }
    default:
      return state;
  }
}

function reduceDiagnostics(state: RunState, eventType: string, data: Dict): RunState {
  switch (eventType) {
    case "evidence.updated": {
      const evidence = isObj(data.evidence) ? (data.evidence as unknown as Evidence) : null;
      if (!evidence?.evidence_id) return state;
      return { ...state, evidence: { ...state.evidence, [evidence.evidence_id]: evidence } };
    }
    case "usage":
      return isObj(data.usage) ? { ...state, usage: data.usage as unknown as Usage } : state;
    case "error":
      return isObj(data.error)
        ? { ...state, errors: [...state.errors, data.error as unknown as ProtocolError] }
        : state;
    default:
      return state;
  }
}

// ───────────────────────────────────────────────────────────────────────────
// The reducer
// ───────────────────────────────────────────────────────────────────────────

/** `run.seq` is the resume watermark — every durable frame advances it. */
function bumpSeq(state: RunState, frame: ProtocolFrame): RunState {
  const seq = num(frame.seq);
  if (seq === undefined || seq <= state.run.seq) return state;
  return { ...state, run: { ...state.run, seq } };
}

/**
 * Fold one frame into RunState. Pure — same inputs, same output, no ambient
 * reads. `state` is `null` before the first `state.snapshot`.
 */
export function reduceProtocolFrame(state: RunState | null, frame: ProtocolFrame): ReduceOutcome {
  // A frame is whatever `JSON.parse` returned for one `data:` line, so it can be
  // any JSON value — `null` included. Dereferencing that is a TypeError thrown
  // out of the reducer, which is exactly the thing this function promises never
  // to do; a non-object frame is unreadable, so it is ignored like an unknown
  // event rather than raising a desync the server could not have caused.
  if (!isObj(frame)) return { state, desync: false };
  const eventType = str(frame.event_type);
  if (!eventType || TRANSPORT_EVENT_TYPES.has(eventType)) return { state, desync: false };

  const data = asObj(frame.data);

  if (eventType === "state.snapshot") {
    // Wholesale. The snapshot IS the state (§1.4.3) and replaces whatever this
    // client had accumulated, including any drift it did not know about.
    return { state: normalizeRunState(frame.data), desync: false };
  }

  if (!state) {
    // Durable frames before the mandatory first snapshot. Not a desync: the
    // snapshot is on its way and carries everything these frames describe.
    return { state: null, desync: false };
  }

  if (eventType === "state.delta") {
    /*
     * ── WHICH DELTAS APPLY, MEASURED AGAINST A REAL BACKEND ─────────────────
     *
     * This was strict `base_seq === run.seq` in both directions, and it desynced on EVERY FRAME
     * against the deployed server: a 30-second run issued 18 `GET /state` reads. The capture:
     *
     *     message.part_delta   seq=6215181
     *     state.delta          seq=6215181  base_seq=6215180
     *
     * A typed event and its paired `state.delta` SHARE one `seq` (handoff gotcha 3), and the delta's
     * `base_seq` is the state before that shared transition. So once the typed twin is applied,
     * `run.seq` is already 6215181 while the delta reports `base_seq: 6215180` — behind by exactly
     * one, by design, on every frame that has a twin.
     *
     * A delta declares state(base_seq) --patch--> state(frame.seq), so both numbers are needed:
     *
     *   base_seq === run.seq                        APPLY.  The ordinary case — no twin, or the
     *                                                       delta arrived first. The captured stream
     *                                                       has plenty (seq 6215185, 6215188).
     *   frame.seq === run.seq                       APPLY.  The twin. Its `replace` ops carry the
     *     && base_seq === run.seq - 1                       server's authoritative subtrees
     *                                                       (`/usage/tool_calls`, `/errors`) that the
     *                                                       typed events do not all cover, so
     *                                                       applying beats skipping.
     *   frame.seq <= run.seq  (not the twin)        SKIP.   Already applied — a reconnect replay on
     *                                                       top of the mandatory snapshot. No
     *                                                       reconcile: nothing would change.
     *   frame.seq  >  run.seq                       DESYNC. Its base is not our state, so frames in
     *     && base_seq !== run.seq                           between were missed. What `GET /state` is
     *                                                       for.
     */
    const baseSeq = num(data.base_seq);
    const localSeq = state.run.seq;
    const frameSeq = num(frame.seq);
    if (baseSeq !== undefined) {
      /*
       * A delta declares one transition: state(base_seq) --patch--> state(frame.seq). BOTH numbers
       * decide what to do with it; reading only `base_seq` is what got this wrong twice.
       */
      const alreadyApplied = frameSeq !== undefined && frameSeq <= localSeq;
      const isTwin = alreadyApplied && frameSeq === localSeq && baseSeq === localSeq - 1;

      if (baseSeq !== localSeq && !isTwin) {
        if (alreadyApplied) {
          /*
           * The whole transition is behind us — a replay after a reconnect, which replays
           * `seq > cursor` on top of the mandatory snapshot. Reconciling here would be a request
           * that changes nothing, and applying it could double-apply an `add`.
           */
          return { state, desync: false };
        }
        /*
         * `frame.seq` is ahead of us but its base is not our state: frames between the two were
         * missed. This is the case `GET /state` exists for.
         */
        return {
          state,
          desync: true,
          reason: `delta base_seq ${baseSeq} does not match local seq ${localSeq}`,
        };
      }

      if (isTwin) {
        /*
         * An `add` in a twin would append what the typed event already added. Not observed on the
         * wire — every array op seen is a whole-array `replace` (handoff gotcha 5) — and refused to
         * the reconcile path rather than trusted.
         */
        const twinOps = isArr(data.patch) ? (data.patch as PatchOperation[]) : null;
        if (!twinOps) return { state, desync: true, reason: "state.delta without a patch array" };
        if (twinOps.some((op) => op && (op as { op?: string }).op === "add")) {
          return {
            state,
            desync: true,
            reason: "twin delta carries an `add` op, which would double-apply — reconciling instead",
          };
        }
      }
    }
    const patch = isArr(data.patch) ? (data.patch as PatchOperation[]) : null;
    if (!patch) return { state, desync: true, reason: "state.delta without a patch array" };
    try {
      // No re-normalization here on purpose: the state being patched was
      // already normal, and rebuilding it would throw away the structural
      // sharing `applyJsonPatch` just went to the trouble of preserving —
      // which is the whole reason unrelated selectors skip a re-render.
      return { state: bumpSeq(applyJsonPatch(state, patch), frame), desync: false };
    } catch (err) {
      return { state, desync: true, reason: err instanceof Error ? err.message : "patch failed" };
    }
  }

  const family = eventType.slice(0, eventType.indexOf("."));
  let next = state;
  switch (family) {
    case "lifecycle":
      next = reduceLifecycle(state, eventType, data, frame);
      break;
    case "message":
      next = reduceMessage(state, eventType, data, frame);
      break;
    case "tool":
      next = reduceTool(state, eventType, data);
      break;
    case "task":
      next = reduceTask(state, eventType, data, frame);
      break;
    case "plan":
      next = reducePlan(state, eventType, data);
      break;
    case "changeset":
      next = reduceChangeset(state, eventType, data);
      break;
    case "deploy":
      next = reduceDeploy(state, eventType, data);
      break;
    case "interaction":
      next = reduceInteraction(state, eventType, data);
      break;
    default:
      // `evidence.updated`, `usage`, `error` — the dotted-family split does not
      // apply to the two bare names, so diagnostics is the fallthrough.
      next = reduceDiagnostics(state, eventType, data);
      break;
  }
  return { state: bumpSeq(next, frame), desync: false };
}

/**
 * Fold a sequence of frames. Used by the tests and by any caller replaying a
 * durable tail; `desync` is sticky so one bad delta is not washed out by the
 * frames after it.
 */
export function replayProtocolFrames(
  state: RunState | null,
  frames: readonly ProtocolFrame[],
): ReduceOutcome {
  let current = state;
  let desync = false;
  let reason: string | undefined;
  for (const frame of frames) {
    const outcome = reduceProtocolFrame(current, frame);
    current = outcome.state;
    if (outcome.desync && !desync) {
      desync = true;
      reason = outcome.reason;
    }
  }
  return { state: current, desync, reason };
}

/** True for the frame that closes the stream (§1.4.7). */
export function isTerminalProtocolFrame(frame: ProtocolFrame): boolean {
  return isObj(frame) && frame.event_type === TERMINAL_EVENT_TYPE;
}
